import Phaser from 'phaser';
import { GAME_WIDTH, GAME_HEIGHT } from '@/config/gameConfig';
import { THEME } from '@/config/theme';
import { drawArcadeBackground } from '@/utils/arcadeBackground';
import type { NoteTrack } from '@/objects/NoteTrack';
import { VFXManager } from '@/objects/VFXManager';
import { AudioManager } from '@/systems/AudioManager';
import { DIFFICULTIES, getInputThresholdMs, type DifficultyConfig } from '@/utils/timing';
import type { MorseSymbol } from '@/utils/morse';
import { calculateVerticalLayouts, type LaneLayout } from '@/game/LaneLayoutManager';
import { PlayerLane } from '@/game/PlayerLane';
import type { MultiplayerGameData } from '@/types/multiplayer';
import { getOnlineRoom } from '@/online/session';
import { getLocalSeatColor } from '@/utils/seatColors';
import { isLandscape, isMobileDevice, onViewportChanged } from '@/utils/mobile';
import { setDiscordMatchPresence } from '@/discord';
import { captureError } from '@/sentry';
import { trackGameStart, trackMatchStart } from '@/services/analytics';

export class TelephoneGameScene extends Phaser.Scene {
  private message!: string;
  private difficultyConfig!: DifficultyConfig;
  private difficultyKey: string = 'medium';
  private players!: MultiplayerGameData['players'];
  private online: MultiplayerGameData['online'] | null = null;
  private onlineDisposers: Array<() => void> = [];
  private onlineWaitingText: Phaser.GameObjects.Text | null = null;
  private pendingRemoteEvents: Array<
    { type: 'down'; seat: number; t: number }
    | { type: 'up'; seat: number; t: number; duration: number }
  > = [];
  private localDecodedSeqBySeat = new Map<number, number>();
  private nextExpectedDecodedSeqBySeat = new Map<number, number>();
  private bufferedDecodedLettersBySeat = new Map<number, Map<number, string>>();
  private isMobile: boolean = false;
  private mobileInputBlocked: boolean = false;
  private mobileOverlay: Phaser.GameObjects.Container | null = null;

  private targetMessages: string[] = [];
  private telephoneCascadeEnabled: boolean = true;

  private lanes: PlayerLane[] = [];
  private laneLayouts: LaneLayout[] = [];

  private vfxManager!: VFXManager;
  private audioManager!: AudioManager;

  private isPlaying: boolean = false;
  private gameStartTime: number = 0;
  private notesFinished: boolean = false;
  private notesFinishedTime: number = 0;
  private inputDisabled: boolean = false;
  private inputDisabledTime: number = 0;
  private readonly INPUT_GRACE_PERIOD = 2000;
  private activeToneHolders: number = 0;

  constructor() {
    super({ key: 'TelephoneGameScene' });
  }

  init(data: MultiplayerGameData): void {
    if (data.mode !== 'telephone') {
      throw new Error('TelephoneGameScene requires mode="telephone"');
    }
    if (!data.difficulty) {
      throw new Error('TelephoneGameScene requires difficulty');
    }
    if (!DIFFICULTIES[data.difficulty]) {
      throw new Error(`Invalid difficulty: "${data.difficulty}"`);
    }

    this.message = data.message;
    this.players = data.players;
    this.difficultyKey = data.difficulty;
    this.difficultyConfig = DIFFICULTIES[this.difficultyKey];
    this.online = data.online?.enabled ? data.online : null;

    this.targetMessages = data.players.map((_, i) => i === 0 ? this.message : '');
    this.telephoneCascadeEnabled = true;

    this.isPlaying = false;
    this.gameStartTime = 0;
    this.notesFinished = false;
    this.notesFinishedTime = 0;
    this.inputDisabled = false;
    this.inputDisabledTime = 0;
    this.activeToneHolders = 0;
    this.lanes = [];
    this.onlineDisposers = [];
    this.onlineWaitingText = null;
    this.pendingRemoteEvents = [];
    this.localDecodedSeqBySeat = new Map();
    this.nextExpectedDecodedSeqBySeat = new Map();
    this.bufferedDecodedLettersBySeat = new Map();
    this.isMobile = isMobileDevice();
    this.mobileInputBlocked = false;
    this.mobileOverlay = null;
  }

  create(): void {
    // Track game start
    trackGameStart({ mode: 'telephone', difficulty: this.difficultyKey });
    trackMatchStart('telephone', this.players.length);

    // Update Discord presence to "In match" for online games
    if (this.online?.enabled && this.online.roomId) {
      setDiscordMatchPresence({
        mode: 'telephone',
        difficulty: this.difficultyKey,
        roomId: this.online.roomId,
        playerCount: this.players.length,
        maxPlayers: 4,
        playerNames: this.players.map((p) => p.name),
      });
    }

    this.cameras.main.fadeIn(300, 5, 7, 22);
    this.createBackground();

    this.laneLayouts = calculateVerticalLayouts(this.players.length);

    this.vfxManager = new VFXManager(this);
    this.audioManager = AudioManager.get(this);

    this.createLanes();
    this.attachOnlineInputRelay();
    this.attachOnlineDecodedLetterRelay();
    this.setupMobileOrientationGate();
    this.audioManager.playSfx('start_game');

    if (this.online?.enabled) {
      Promise.all(this.lanes.map(l => l.showCountdownAt(this.online!.startAt)))
        .then(() => this.startGame());
    } else {
      Promise.all(this.lanes.map(l => l.showCountdown()))
        .then(() => this.startGame());
    }

    this.input.keyboard?.on('keydown-ESC', () => {
      this.endGame();
    });
  }

  update(time: number, delta: number): void {
    if (!this.isPlaying) return;

    for (const lane of this.lanes) {
      lane.update(time, delta);
    }

    this.checkPassedNotes();

    const tracksComplete = this.lanes.every(lane => lane.noteTrack.isComplete());
    if (tracksComplete && !this.notesFinished) {
      this.notesFinished = true;
      this.notesFinishedTime = time;
    }

    if (this.notesFinished && !this.inputDisabled) {
      const timeSinceNotesFinished = time - this.notesFinishedTime;
      if (timeSinceNotesFinished > this.INPUT_GRACE_PERIOD) {
        this.inputDisabled = true;
        this.inputDisabledTime = time;
        this.telephoneCascadeEnabled = false;
        for (const lane of this.lanes) {
          lane.stopInput();
        }
        this.activeToneHolders = 0;
        this.audioManager.stopTone();
      }
    }

    if (this.inputDisabled) {
      const timeSinceDisabled = time - this.inputDisabledTime;
      const lanesEmpty = this.lanes.every(lane => lane.sentTrack.isEmpty());
      if (timeSinceDisabled > 250 && lanesEmpty) {
        this.endGame();
      }
    }
  }

  shutdown(): void {
    this.onlineDisposers.forEach(dispose => {
      try {
        dispose();
      } catch (error) {
        captureError(error, { scope: 'telephoneGame', action: 'shutdown.dispose' });
      }
    });
    this.onlineDisposers = [];
    this.onlineWaitingText?.destroy();
    this.onlineWaitingText = null;
    this.mobileOverlay?.destroy(true);
    this.mobileOverlay = null;
    this.pendingRemoteEvents = [];
    this.bufferedDecodedLettersBySeat.clear();
    this.nextExpectedDecodedSeqBySeat.clear();
    this.localDecodedSeqBySeat.clear();

    const destroyedTracks = new Set<NoteTrack>();
    for (const lane of this.lanes) {
      if (!destroyedTracks.has(lane.noteTrack)) {
        lane.noteTrack.destroy();
        destroyedTracks.add(lane.noteTrack);
      }
      lane.destroy();
    }
    this.vfxManager?.destroy();
    this.audioManager?.destroy();
  }

  private createBackground(): void {
    drawArcadeBackground(this);

    // Keep the HUD compact; lane-local HUD carries player-specific info.
    const hudHeight = 110;
    const hudBar = this.add.rectangle(GAME_WIDTH / 2, hudHeight / 2, GAME_WIDTH, hudHeight, THEME.colors.backgroundLightHex, 0.68);
    hudBar.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    hudBar.setDepth(-1);

    const footer = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT - 40, GAME_WIDTH, 80, THEME.colors.backgroundLightHex, 0.68);
    footer.setStrokeStyle(2, THEME.colors.backgroundPanelHex);
    footer.setDepth(-1);

    this.add.text(GAME_WIDTH / 2, 18, `TELEPHONE — ${this.difficultyConfig.name} Mode`, {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5, 0);

    const labelsY = hudHeight + 12;
    this.add.text(50, labelsY, 'TRANSMITTED:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0, 0);

    this.add.text(GAME_WIDTH - 50, labelsY, 'TARGET:', {
      fontFamily: THEME.fonts.pixelBold,
      fontSize: THEME.sizes.body,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(1, 0);

    this.add.text(GAME_WIDTH - 20, GAME_HEIGHT - 20, 'ESC = quit', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.small,
      color: THEME.colors.text,
    }).setOrigin(1, 0.5);
  }

  private createLanes(): void {
    const localSeatIndices = this.online?.enabled
      ? (this.online.localSeatIndices ?? [])
      : this.players.map((_, idx) => idx);

    this.lanes = this.laneLayouts.map((layout, index) => {
      const player = this.players[index];
      const borderColor = getLocalSeatColor(layout.playerId, this.players.length, localSeatIndices)
        ?? THEME.colors.borderHex;
      const noteMessage = layout.playerId === 0 ? this.message : '';

      const lane = new PlayerLane(this, {
        layout,
        playerName: player.name,
        isSinglePlayer: false,
        inputBinding: player.inputBinding,
        difficulty: this.difficultyConfig,
        noteMessage,
        targetMessage: this.targetMessages[layout.playerId] ?? '',
        borderColor,
        vfxManager: this.vfxManager,
        audioManager: this.audioManager,
        onLetterDecoded: (letter) => this.onLetterDecoded(layout.playerId, letter),
        onScoreEvent: this.online?.enabled
          ? (payload) => {
              const room = getOnlineRoom();
              room?.send('score_update', payload);
            }
          : undefined,
      });

      lane.inputManager.onInputDown((time) => this.handleInputDown(lane.playerId, time));
      lane.inputManager.onInputUp((time, duration) => this.handleInputUp(lane.playerId, time, duration));
      return lane;
    });
  }

  private onLetterDecoded(sourcePlayerId: number, letter: string): void {
    if (!this.online?.enabled) {
      this.handleTelephoneLetter(sourcePlayerId, letter);
      return;
    }
    if (!this.isLocalOnlineSeat(sourcePlayerId)) return;

    const normalized = letter.trim().toUpperCase();
    if (!/^[A-Z0-9]$/.test(normalized)) return;

    const seq = this.localDecodedSeqBySeat.get(sourcePlayerId) ?? 0;
    this.localDecodedSeqBySeat.set(sourcePlayerId, seq + 1);
    getOnlineRoom()?.send('decoded_letter', { seat: sourcePlayerId, seq, letter: normalized });
  }

  private startGame(): void {
    this.isPlaying = true;
    this.gameStartTime = this.time.now;
    for (const lane of this.lanes) {
      lane.noteTrack.start(this.gameStartTime);
      lane.sentTrack.start();
      if (!this.online?.enabled || this.isLocalOnlineSeat(lane.playerId)) {
        lane.inputManager.enable();
      }
    }

    const pending = this.pendingRemoteEvents;
    this.pendingRemoteEvents = [];
    pending.forEach((evt) => {
      if (evt.type === 'down') {
        this.applyInputDown(evt.seat, this.gameStartTime + evt.t, true);
      } else {
        this.applyInputUp(evt.seat, this.gameStartTime + evt.t, evt.duration, true);
      }
    });
  }

  private handleInputDown(playerId: number, time: number): void {
    this.applyInputDown(playerId, time, false);
  }

  private handleInputUp(playerId: number, time: number, duration: number): void {
    this.applyInputUp(playerId, time, duration, false);
  }

  private applyInputDown(playerId: number, time: number, isRemote: boolean): void {
    if (!this.isPlaying) return;
    if (this.online?.enabled && !isRemote && !this.isLocalOnlineSeat(playerId)) return;
    if (this.mobileInputBlocked && !isRemote) return;

    const lane = this.lanes.find(l => l.playerId === playerId);
    if (!lane) return;

    lane.setHitZonePressed(true);

    if (!isRemote) {
      this.activeToneHolders++;
      if (this.activeToneHolders === 1) {
        this.audioManager.startTone();
      }
    }

    const downResult = lane.handleKeyDown(time);

    if (this.online?.enabled && !isRemote) {
      const elapsed = Math.max(0, Math.floor(time - this.gameStartTime));
      getOnlineRoom()?.send('input_down', { seat: playerId, t: elapsed });
    }

    if (!downResult.note) {
      return;
    }
  }

  private applyInputUp(playerId: number, time: number, duration: number, isRemote: boolean): void {
    if (!this.isPlaying) return;
    if (this.online?.enabled && !isRemote && !this.isLocalOnlineSeat(playerId)) return;
    if (this.mobileInputBlocked && !isRemote) return;

    const lane = this.lanes.find(l => l.playerId === playerId);
    if (!lane) return;

    lane.setHitZonePressed(false);

    if (!isRemote) {
      this.activeToneHolders = Math.max(0, this.activeToneHolders - 1);
      if (this.activeToneHolders === 0) {
        this.audioManager.stopTone();
      }
    }

    const threshold = getInputThresholdMs(this.difficultyConfig);
    const playerSymbol: MorseSymbol = duration < threshold ? '.' : '-';

    const upResult = lane.handleKeyUp(time, duration, playerSymbol);

    if (!upResult.note) {
      return;
    }

    if (this.online?.enabled && !isRemote) {
      const elapsed = Math.max(0, Math.floor(time - this.gameStartTime));
      getOnlineRoom()?.send('input_up', { seat: playerId, t: elapsed, duration: Math.max(0, Math.floor(duration)) });
    }
  }

  private checkPassedNotes(): void {
    for (const lane of this.lanes) {
      lane.checkPassedNotes();
    }
  }

  private handleTelephoneLetter(sourcePlayerId: number, letter: string): void {
    if (!this.telephoneCascadeEnabled) return;

    const targetPlayerId = sourcePlayerId + 1;
    if (targetPlayerId >= this.lanes.length) return;

    const targetLane = this.lanes[targetPlayerId];
    const added = targetLane.noteTrack.appendLetterFromRightEdge(letter);
    targetLane.addTotalNotes(added);

    const nextTarget = (this.targetMessages[targetPlayerId] ?? '') + letter;
    this.targetMessages[targetPlayerId] = nextTarget;
    targetLane.setTargetMessage(nextTarget);
  }

  private queueDecodedLetter(seat: number, seq: number, letter: string): void {
    const expected = this.nextExpectedDecodedSeqBySeat.get(seat) ?? 0;
    if (seq < expected) return;

    let buffer = this.bufferedDecodedLettersBySeat.get(seat);
    if (!buffer) {
      buffer = new Map();
      this.bufferedDecodedLettersBySeat.set(seat, buffer);
    }
    buffer.set(seq, letter);

    let next = expected;
    while (buffer.has(next)) {
      const nextLetter = buffer.get(next);
      buffer.delete(next);
      if (nextLetter) {
        this.handleTelephoneLetter(seat, nextLetter);
      }
      next++;
    }
    this.nextExpectedDecodedSeqBySeat.set(seat, next);
  }

  private endGame(): void {
    this.isPlaying = false;
    this.telephoneCascadeEnabled = false;
    this.audioManager.stopTone();
    this.activeToneHolders = 0;

    for (const lane of this.lanes) {
      lane.stopInput();
    }

    const playerResults = this.lanes.map((lane) => {
      const r = lane.getResults();
      return {
        playerId: lane.playerId,
        name: this.players[lane.playerId]?.name ?? `Player ${lane.playerId + 1}`,
        score: r.score,
        accuracy: r.accuracy,
        grade: r.grade,
        maxStreak: r.maxStreak,
        decodedMessage: lane.sentTrack.getFormattedDecodedMessage(),
        targetMessage: this.targetMessages[lane.playerId] ?? '',
      };
    });

    if (this.online?.enabled) {
      this.reportOnlineResultsAndWait(playerResults);
      return;
    }

    this.cameras.main.fadeOut(300, 5, 7, 22);
    this.cameras.main.once('camerafadeoutcomplete', () => {
      this.scene.start('ResultScene', {
        mode: 'telephone',
        players: playerResults,
        originalMessage: this.message,
        setup: {
          mode: 'telephone',
          players: this.players,
          difficulty: this.difficultyKey,
        },
      });
    });
  }

  private reportOnlineResultsAndWait(playerResults: Array<{
    playerId: number;
    name: string;
    score: number;
    accuracy: number;
    grade: string;
    maxStreak: number;
    decodedMessage: string;
    targetMessage: string;
  }>): void {
    const room = getOnlineRoom();
    const localSeatIndices = this.online?.localSeatIndices ?? [];

    if (room) {
      const offMatchEnded = room.onMessage('match_ended', (payload: unknown) => {
        const parsed = payload as { results?: unknown } | null;
        const results = Array.isArray(parsed?.results) ? parsed?.results as Array<unknown> : [];
        const players = results.map((item) => {
          const rec = item as {
            seat?: unknown;
            name?: unknown;
            score?: unknown;
            accuracy?: unknown;
            grade?: unknown;
            maxStreak?: unknown;
            decodedMessage?: unknown;
          } | null;
          const seat = typeof rec?.seat === 'number' ? rec.seat : 0;
          return {
            playerId: seat,
            name: typeof rec?.name === 'string' ? rec.name : `Player ${seat + 1}`,
            score: typeof rec?.score === 'number' ? rec.score : 0,
            accuracy: typeof rec?.accuracy === 'number' ? rec.accuracy : 0,
            grade: typeof rec?.grade === 'string' ? rec.grade : 'D',
            maxStreak: typeof rec?.maxStreak === 'number' ? rec.maxStreak : 0,
            decodedMessage: typeof rec?.decodedMessage === 'string' ? rec.decodedMessage : '',
            targetMessage: this.targetMessages[seat] ?? '',
          };
        });

        this.cameras.main.fadeOut(300, 5, 7, 22);
        this.cameras.main.once('camerafadeoutcomplete', () => {
          this.scene.start('ResultScene', {
            mode: 'telephone',
            players,
            originalMessage: this.message,
            setup: {
              mode: 'telephone',
              players: this.players,
              difficulty: this.difficultyKey,
              online: {
                enabled: true,
                roomId: this.online?.roomId ?? '',
                localSeatIndices,
              },
            },
          });
        });
      });
      this.onlineDisposers.push(offMatchEnded);
    }

    playerResults
      .filter(r => !this.online?.enabled || localSeatIndices.includes(r.playerId))
      .forEach((r) => {
        room?.send('report_result', {
          seat: r.playerId,
          score: r.score,
          accuracy: r.accuracy,
          grade: r.grade,
          maxStreak: r.maxStreak,
          decodedMessage: r.decodedMessage,
        });
      });

    this.onlineWaitingText?.destroy();
    this.onlineWaitingText = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 30, 'Waiting for other players…', {
      fontFamily: THEME.fonts.pixel,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);
  }

  private isLocalOnlineSeat(seatIndex: number): boolean {
    if (!this.online?.enabled) return true;
    return (this.online.localSeatIndices ?? []).includes(seatIndex);
  }

  private attachOnlineInputRelay(): void {
    if (!this.online?.enabled) return;
    const room = getOnlineRoom();
    if (!room) return;

    const offDown = room.onMessage('input_down', (payload: unknown) => {
      const parsed = payload as { seat?: unknown; t?: unknown } | null;
      const seat = typeof parsed?.seat === 'number' ? parsed.seat : null;
      const t = typeof parsed?.t === 'number' ? parsed.t : null;
      if (seat === null || t === null) return;
      if (this.isLocalOnlineSeat(seat)) return;
      if (!this.isPlaying) {
        this.pendingRemoteEvents.push({ type: 'down', seat, t });
        return;
      }
      this.applyInputDown(seat, this.gameStartTime + t, true);
    });

    const offUp = room.onMessage('input_up', (payload: unknown) => {
      const parsed = payload as { seat?: unknown; t?: unknown; duration?: unknown } | null;
      const seat = typeof parsed?.seat === 'number' ? parsed.seat : null;
      const t = typeof parsed?.t === 'number' ? parsed.t : null;
      const duration = typeof parsed?.duration === 'number' ? parsed.duration : null;
      if (seat === null || t === null || duration === null) return;
      if (this.isLocalOnlineSeat(seat)) return;
      if (!this.isPlaying) {
        this.pendingRemoteEvents.push({ type: 'up', seat, t, duration });
        return;
      }
      this.applyInputUp(seat, this.gameStartTime + t, duration, true);
    });

    this.onlineDisposers.push(offDown, offUp);
  }

  private attachOnlineDecodedLetterRelay(): void {
    if (!this.online?.enabled) return;
    const room = getOnlineRoom();
    if (!room) return;

    const offDecoded = room.onMessage('decoded_letter', (payload: unknown) => {
      const parsed = payload as { seat?: unknown; seq?: unknown; letter?: unknown } | null;
      const seat = typeof parsed?.seat === 'number' ? parsed.seat : null;
      const seq = typeof parsed?.seq === 'number' ? parsed.seq : null;
      const letter = typeof parsed?.letter === 'string' ? parsed.letter : null;
      if (seat === null || seq === null || !letter) return;
      const normalized = letter.trim().toUpperCase();
      if (!/^[A-Z0-9]$/.test(normalized)) return;
      this.queueDecodedLetter(seat, Math.floor(seq), normalized);
    });

    this.onlineDisposers.push(offDecoded);
  }

  private setupMobileOrientationGate(): void {
    if (!this.isMobile) return;

    const overlay = this.add.container(0, 0);
    overlay.setDepth(5000);

    const bg = this.add.rectangle(GAME_WIDTH / 2, GAME_HEIGHT / 2, GAME_WIDTH, GAME_HEIGHT, 0x000000, 0.78);
    const title = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 - 40, 'Rotate to landscape', {
      fontFamily: THEME.fonts.heading,
      fontSize: THEME.sizes.subheading,
      color: THEME.colors.cyan,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thickness,
    }).setOrigin(0.5);

    const subtitle = this.add.text(GAME_WIDTH / 2, GAME_HEIGHT / 2 + 10, 'to play', {
      fontFamily: THEME.fonts.body,
      fontSize: THEME.sizes.bodyLarge,
      color: THEME.colors.text,
      stroke: THEME.stroke.color,
      strokeThickness: THEME.stroke.thicknessThin,
    }).setOrigin(0.5);

    overlay.add([bg, title, subtitle]);
    this.mobileOverlay = overlay;

    const apply = (): void => {
      const shouldBlock = !isLandscape();
      if (shouldBlock === this.mobileInputBlocked) {
        this.mobileOverlay?.setVisible(shouldBlock);
        return;
      }

      this.mobileInputBlocked = shouldBlock;
      this.mobileOverlay?.setVisible(shouldBlock);
      if (shouldBlock) {
        this.cancelLocalInputs();
      }
    };

    apply();
    const offResize = onViewportChanged(apply);
    this.onlineDisposers.push(offResize);

    if (typeof document !== 'undefined') {
      const onVisibility = () => {
        if (document.hidden) this.cancelLocalInputs();
      };
      document.addEventListener('visibilitychange', onVisibility);
      this.onlineDisposers.push(() => document.removeEventListener('visibilitychange', onVisibility));
    }
    if (typeof window !== 'undefined') {
      const onBlur = () => this.cancelLocalInputs();
      window.addEventListener('blur', onBlur);
      this.onlineDisposers.push(() => window.removeEventListener('blur', onBlur));
    }
  }

  private cancelLocalInputs(): void {
    this.activeToneHolders = 0;
    this.audioManager?.stopTone();
    for (const lane of this.lanes) {
      if (this.online?.enabled && !this.isLocalOnlineSeat(lane.playerId)) continue;
      lane.inputManager.cancelPress();
      lane.sentTrack.cancelActiveSymbol();
    }
  }
}
